import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import {
  FileText,
  Gauge,
  Gavel,
  GraduationCap,
  Home,
  Lock,
  ShieldCheck,
  Sparkles,
  User2,
  Video,
} from "lucide-react";
import { useAuth, useRole } from "@/contexts/AuthContext";
import { listMyCases, type CaseSummary } from "@/lib/api";
import { getDpoStatus } from "@/lib/dpoApi";

interface ArbiterSidebarProps {
  /** Appelle quand un lien est cliques (utile pour fermer le drawer mobile). */
  onNavigate?: () => void;
}

// Sidebar persistante du Workspace Arbitre. Charge la liste des dossiers
// au mount via listMyCases() et expose les liens d'application principale
// + chaque dossier individuellement.
//
// Defensive sur la fetch : si l'API echoue (Art.52, 503, network), on
// affiche "Aucun dossier" plutot que de crasher la sidebar.
export function ArbiterSidebar({ onNavigate }: ArbiterSidebarProps) {
  const { session } = useAuth();
  const role = useRole();
  const [cases, setCases] = useState<CaseSummary[] | null>(null);
  // Habilitation DPO (autorite serveur) : le lien Conformite n'est affiche
  // qu'au DPO designe / admin, pas a tous les arbitres.
  const [dpoAuthorized, setDpoAuthorized] = useState(false);
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    listMyCases()
      .then((data) => {
        if (!cancelled) setCases(data);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[ArbiterSidebar] listMyCases failed", err);
        if (!cancelled) setCases([]);
      });
    return () => {
      cancelled = true;
    };
  }, [session]);

  useEffect(() => {
    // Seuls arbitre/admin peuvent etre DPO/oversight ; on evite un appel
    // inutile pour les autres. En cas d'erreur (endpoint indisponible) le
    // lien reste masque (le DPO peut tout de meme ouvrir /dpo directement).
    if (!session || (role !== "ARBITRE" && role !== "ADMIN")) return;
    let cancelled = false;
    getDpoStatus()
      .then((s) => {
        if (!cancelled) setDpoAuthorized(s.authorized);
      })
      .catch(() => {
        if (!cancelled) setDpoAuthorized(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session, role]);

  const goTo = (path: string) => {
    setLocation(path);
    onNavigate?.();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border">
        <button
          onClick={() => goTo("/")}
          className="text-caption-arbitre text-left"
          style={{ color: "var(--proof-gold)" }}
        >
          ← GSF Arbitral AI
        </button>
        {role && (
          <div
            className="mt-1 text-[10px] font-bold tracking-widest"
            style={{ color: "var(--proof-gold)", letterSpacing: "0.18em" }}
          >
            {role}
          </div>
        )}
      </div>

      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
        <SidebarLink
          icon={<Home size={14} />}
          active={location === "/dashboard"}
          onClick={() => goTo("/dashboard")}
        >
          Tableau de bord
        </SidebarLink>
        <SidebarLink
          icon={<Gauge size={14} />}
          active={location.startsWith("/cockpit")}
          onClick={() => goTo("/cockpit")}
        >
          Cockpit arbitre
        </SidebarLink>
        <SidebarLink
          icon={<Sparkles size={14} />}
          active={location.startsWith("/recherche")}
          onClick={() => goTo("/recherche")}
        >
          Recherche IA
        </SidebarLink>
        <SidebarLink
          icon={<User2 size={14} />}
          active={location.startsWith("/profil")}
          onClick={() => goTo("/profil")}
        >
          Mon profil
        </SidebarLink>
        <SidebarLink
          icon={<GraduationCap size={14} />}
          active={location.startsWith("/formation-arbitre")}
          onClick={() => goTo("/formation-arbitre")}
        >
          Formation
        </SidebarLink>
        {/* Supervision DPO : affiche uniquement si le serveur confirme
            l'habilitation (est_dpo / ADMIN). */}
        {dpoAuthorized && (
          <SidebarLink
            icon={<ShieldCheck size={14} />}
            active={location.startsWith("/dpo")}
            onClick={() => goTo("/dpo")}
          >
            Conformité (DPO)
          </SidebarLink>
        )}

        <div
          className="mt-4 mb-1 px-2 text-caption-arbitre"
          style={{ color: "var(--proof-gold)" }}
        >
          Mes dossiers
        </div>

        {cases === null && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground italic">
            Chargement…
          </div>
        )}
        {cases && cases.length === 0 && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground italic">
            Aucun dossier
          </div>
        )}
        {cases &&
          cases.map((c) => {
            const casePath = `/cases/${c.id}`;
            const audiencePath = `/audiences/${c.id}`;
            const decisionsPath = `/cases/${c.id}/decisions`;
            const deliberationPath = `/cases/${c.id}/deliberation`;
            return (
              <div key={c.id} className="mb-1">
                <SidebarLink
                  icon={<FileText size={14} />}
                  active={location === casePath}
                  onClick={() => goTo(casePath)}
                  truncated
                  title={`${c.numero_gsf} — ${c.objet_litige}`}
                >
                  <span className="font-mono text-[12px]">{c.numero_gsf}</span>
                </SidebarLink>
                {/* Sous-liens compacts par dossier (audience + decisions) */}
                <div className="ml-5 mt-0.5 space-y-0.5">
                  <SidebarLink
                    icon={<Video size={11} />}
                    active={location === audiencePath}
                    onClick={() => goTo(audiencePath)}
                    sub
                  >
                    Audience
                  </SidebarLink>
                  <SidebarLink
                    icon={<Gavel size={11} />}
                    active={location === decisionsPath}
                    onClick={() => goTo(decisionsPath)}
                    sub
                  >
                    Décisions
                  </SidebarLink>
                  <SidebarLink
                    icon={<Lock size={11} />}
                    active={location === deliberationPath}
                    onClick={() => goTo(deliberationPath)}
                    sub
                  >
                    Délibéré
                  </SidebarLink>
                </div>
              </div>
            );
          })}
      </nav>
    </div>
  );
}

function SidebarLink({
  icon,
  children,
  active,
  onClick,
  sub,
  truncated,
  title,
}: {
  icon: ReactNode;
  children: ReactNode;
  active?: boolean;
  onClick: () => void;
  sub?: boolean;
  truncated?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`w-full flex items-center gap-2 px-2 rounded-md transition-colors text-left ${
        sub ? "py-1 text-[12px]" : "py-1.5 text-sm"
      }`}
      style={{
        minHeight: sub ? "1.75rem" : "2.25rem",
        background: active
          ? "color-mix(in oklch, var(--proof-gold) 12%, transparent)"
          : "transparent",
        color: active ? "var(--proof-gold)" : "var(--arbitre-slate-100)",
        border: active
          ? "1px solid color-mix(in oklch, var(--proof-gold) 30%, transparent)"
          : "1px solid transparent",
      }}
    >
      <span className="shrink-0 opacity-70">{icon}</span>
      <span className={truncated ? "truncate flex-1" : "flex-1"}>
        {children}
      </span>
    </button>
  );
}
